import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  useFacilitatorPortalService,
  type FacilitatorDashboardStats,
  type FacilitatorRegistrationView,
  type FacilitatorSessionView,
} from '@actschurch/ccms-sdk';
import { DASHBOARD_POLL_MS } from '../constants';
import { sdkNow } from '@/utils/dateTime';

/**
 * useFacilitatorDashboard — everything the "My Courses" half of the Dashboard needs.
 *
 * Mirrors `useLeaderDashboard`: short-circuits when `enabled` is false so a leader-only
 * user never fires a facilitator request, and uses `Promise.allSettled` so a missing
 * `Facilitator` record hides the section instead of failing the page.
 *
 * Two calls are specced but not yet in the SDK
 * (`_deploy/_newEndPoints/ac-courses-overview.md`) and are detected by capability:
 *  - `myBehindScheduleRegistrations()` — `FacilitatorRegistrationView` has no
 *    `isBehindSchedule`, unlike the admin-side `RegistrationListView`. Until it ships we
 *    derive an approximation from the session list, which is honest but coarser.
 *  - `myIncompleteSessionForms()` — no client-side substitute exists at all, so that
 *    attention item is simply omitted rather than guessed.
 */
export interface UseFacilitatorDashboardResult {
  stats: FacilitatorDashboardStats | null;
  registrations: FacilitatorRegistrationView[];
  sessions: FacilitatorSessionView[];
  sessionsToday: FacilitatorSessionView[];
  /** Sessions past their date that are not marked complete. */
  overdueSessions: FacilitatorSessionView[];
  behindScheduleRegistrations: FacilitatorRegistrationView[];
  /** Null when the SDK has no `myIncompleteSessionForms()` — not the same as "none". */
  incompleteForms: any[] | null;
  loading: boolean;
  refresh: () => Promise<void>;
}

const EMPTY: UseFacilitatorDashboardResult = {
  stats: null,
  registrations: [],
  sessions: [],
  sessionsToday: [],
  overdueSessions: [],
  behindScheduleRegistrations: [],
  incompleteForms: null,
  loading: false,
  refresh: async () => {},
};

/** `SessionStatus` ID for a completed session — negative IDs are terminal/cancelled. */
const SESSION_STATUS_COMPLETED = 1;

export function useFacilitatorDashboard(enabled: boolean): UseFacilitatorDashboardResult {
  const facilitatorPortal = useFacilitatorPortalService();

  const [stats, setStats] = useState<FacilitatorDashboardStats | null>(null);
  const [registrations, setRegistrations] = useState<FacilitatorRegistrationView[]>([]);
  const [sessions, setSessions] = useState<FacilitatorSessionView[]>([]);
  const [serverBehind, setServerBehind] = useState<FacilitatorRegistrationView[] | null>(null);
  const [incompleteForms, setIncompleteForms] = useState<any[] | null>(null);
  const [loading, setLoading] = useState(enabled);

  const fetchAll = useCallback(async (showLoading = true) => {
    if (!enabled) return;
    if (showLoading) setLoading(true);

    const portal = facilitatorPortal as any;

    try {
      const [statsRes, regRes, sessionRes, behindRes, formsRes] = await Promise.allSettled([
        facilitatorPortal.dashboardStats(),
        facilitatorPortal.myRegistrations(),
        facilitatorPortal.mySessions(),
        typeof portal.myBehindScheduleRegistrations === 'function'
          ? portal.myBehindScheduleRegistrations()
          : Promise.reject(new Error('myBehindScheduleRegistrations() not in this SDK version')),
        typeof portal.myIncompleteSessionForms === 'function'
          ? portal.myIncompleteSessionForms()
          : Promise.reject(new Error('myIncompleteSessionForms() not in this SDK version')),
      ]);

      if (statsRes.status === 'fulfilled') setStats(statsRes.value);
      if (regRes.status === 'fulfilled') setRegistrations(regRes.value ?? []);
      if (sessionRes.status === 'fulfilled') setSessions(sessionRes.value ?? []);
      setServerBehind(behindRes.status === 'fulfilled' ? (behindRes.value ?? []) : null);
      setIncompleteForms(formsRes.status === 'fulfilled' ? (formsRes.value ?? []) : null);
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [enabled, facilitatorPortal]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const fetchRef = useRef(fetchAll);
  fetchRef.current = fetchAll;

  useEffect(() => {
    if (!enabled) return undefined;
    const id = window.setInterval(() => { fetchRef.current(false); }, DASHBOARD_POLL_MS);
    return () => window.clearInterval(id);
  }, [enabled]);

  const sessionsToday = useMemo(() => sessions.filter((s) => s.isToday), [sessions]);

  const overdueSessions = useMemo(() => {
    const now = Number(sdkNow());
    return sessions
      .filter((s) => s.sessionDateTime < now && s.statusID !== SESSION_STATUS_COMPLETED && s.statusID >= 0)
      .sort((a, b) => a.sessionDateTime - b.sessionDateTime);
  }, [sessions]);

  /**
   * Server list when available; otherwise infer from overdue sessions, which is the same
   * signal `RegistrationListView.isBehindSchedule` is computed from.
   */
  const behindScheduleRegistrations = useMemo(() => {
    if (serverBehind) return serverBehind;
    const behindIds = new Set(overdueSessions.map((s) => s.registrationID));
    return registrations.filter((r) => behindIds.has(r.registrationID));
  }, [serverBehind, overdueSessions, registrations]);

  const refresh = useCallback(() => fetchAll(false), [fetchAll]);

  if (!enabled) return EMPTY;

  return {
    stats, registrations, sessions, sessionsToday, overdueSessions,
    behindScheduleRegistrations, incompleteForms, loading, refresh,
  };
}

export default useFacilitatorDashboard;
